/**
 * Route constants and utilities for building URLs with query parameters.
 */

export const ROUTES = {
  DASHBOARD: '/dashboard',
  ONBOARDING: '/onboarding',
  LOGIN: '/login',
  REGISTER: '/login',
  CHECKOUT: '/checkout',
  SETTINGS: '/settings',
} as const;

/**
 * Protected routes that require authentication.
 * Used by Sentry log filtering, route guards, and middleware.
 */
export const PROTECTED_ROUTES = [
  '/api/',
  '/dashboard',
  '/settings',
  '/messages',
  '/rules',
  '/allowlist',
  '/checkout',
  '/onboarding',
  '/folders',
] as const;

/**
 * Check if a route is protected (requires authentication).
 */
export function isProtectedRoute(pathname: string): boolean {
  return PROTECTED_ROUTES.some((route) => pathname.startsWith(route));
}

export const ROUTE_PARAMS = {
  SIGNUP_SUCCESS: 'signup=success',
  CONNECTED: 'connected=true',
  CHECKOUT_SUCCESS: 'checkout=success',
  CHECKOUT_CANCELED: 'checkout=canceled',
} as const;

/**
 * Build a route URL with query parameters.
 * Preserves existing query parameters from the input path.
 *
 * @param path - Base path (e.g., '/dashboard' or full URL like 'https://example.com/dashboard')
 * @param params - Query parameters as key-value pairs
 * @returns Full URL with query string
 *
 * @example
 * ```ts
 * // Relative path with existing query params
 * buildRoute('/dashboard?other=value', { gmail_account_id: '123' })
 * // Returns: '/dashboard?other=value&gmail_account_id=123'
 *
 * // Full URL
 * buildRoute('https://example.com/dashboard?other=value', { gmail_account_id: '123' })
 * // Returns: 'https://example.com/dashboard?other=value&gmail_account_id=123'
 *
 * // Path without existing params
 * buildRoute('/dashboard', { gmail_account_id: '123' })
 * // Returns: '/dashboard?gmail_account_id=123'
 * ```
 */
export function buildRoute(path: string, params?: Record<string, string>): string {
  if (!params || Object.keys(params).length === 0) {
    return path;
  }

  // Check if path is already a full URL
  let baseUrl: string;
  let pathname: string;
  let existingParams: URLSearchParams | null = null;

  try {
    const url = new URL(path);
    baseUrl = url.origin;
    pathname = url.pathname;
    existingParams = url.searchParams;
  } catch {
    // Not a full URL, try to parse pathname and query separately
    const [pathPart, queryPart] = path.split('?');
    baseUrl = '';
    pathname = pathPart;
    if (queryPart) {
      existingParams = new URLSearchParams(queryPart);
    }
  }

  const url = new URL(pathname, baseUrl || 'http://localhost');

  // Preserve existing query parameters
  if (existingParams) {
    existingParams.forEach((value, key) => {
      url.searchParams.set(key, value);
    });
  }

  // Add/override with new params
  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.set(key, encodeURIComponent(value));
  });

  // If original path was a full URL, return full URL; otherwise just pathname + search
  if (baseUrl) {
    return url.toString();
  }
  return url.pathname + url.search;
}

/**
 * Build a route URL with Gmail account ID query parameter.
 * Preserves existing query parameters if path already has them.
 *
 * @param path - Base path (e.g., '/dashboard' or '/dashboard?other=value')
 * @param accountId - Gmail account ID to include in query params (optional)
 * @returns Path with gmail_account_id query parameter if accountId is provided
 *
 * @example
 * ```ts
 * // Path without existing params
 * buildRouteWithAccount('/dashboard', 'account-123')
 * // Returns: '/dashboard?gmail_account_id=account-123'
 *
 * // Path with existing params (preserved)
 * buildRouteWithAccount('/dashboard?other=value', 'account-123')
 * // Returns: '/dashboard?other=value&gmail_account_id=account-123'
 *
 * // Null/undefined account ID returns path unchanged
 * buildRouteWithAccount('/dashboard', null)
 * // Returns: '/dashboard'
 * ```
 */
export function buildRouteWithAccount(path: string, accountId: string | null | undefined): string {
  if (!accountId) {
    return path;
  }
  return buildRoute(path, { gmail_account_id: accountId });
}
